import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode, GetDefinitionNode } from './testUtil';

type Result = ReturnType<typeof useSnapshotByEnvironment>[0];

export type SnapshotResponse = NonNullable<Result['data']>;

export type RequestedSnapshotAppliedNode = GetAppliedNode<
  'snapshots',
  SnapshotResponse
>;

export type RequestedSnapshotDefinitionNode = GetDefinitionNode<
  'snapshots',
  SnapshotResponse
>;

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSnapshotByUniqueId = graphql(/* GraphQL */ `
  query Snapshot($environmentId: BigInt!, $uniqueIds: [String!], $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      adapterType
      definition {
        lastUpdatedAt
        snapshots(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              uniqueId
              rawCode
            }
          }
        }
      }
      applied {
        lastUpdatedAt
        snapshots(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              fqn
              name
              uniqueId
              tags
              description
              database
              schema
              rawCode
              meta
              alias
              packageName
              filePath
              patchPath
              compiledCode
              projectId
              parents {
                uniqueId
                resourceType
                name
                __typename
              }
              children {
                uniqueId
              }
              executionInfo {
                lastRunId
                lastRunStatus
                executeCompletedAt
                lastJobDefinitionId
                executionTime
              }
              tests {
                uniqueId
                name
                columnName
                executionInfo {
                  lastRunStatus
                  lastRunError
                }
                testMetadata {
                  name
                  kwargs
                }
              }
              catalog {
                type
                primaryKey
                columns {
                  type
                  name
                  comment
                  tags
                  description
                  descriptionOriginColumnName
                  descriptionOriginResourceUniqueId
                  meta
                }
                rowCountStat
                bytesStat
                stats {
                  id
                  label
                  value
                  description
                  include
                }
              }
            }
          }
        }
      }
    }
  }
`);

type SnapshotByEnvironmentParams = {
  environmentId: number | undefined;
  uniqueId: string;
  userId: number | 'loading' | null;
  pause?: boolean;
};

export const useSnapshotByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: SnapshotByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetSnapshotByUniqueId,
    variables: {
      environmentId,
      uniqueIds: [uniqueId],
      userId: userId as number | null,
    },
    pause: pause || !environmentId || !uniqueId,
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
    definition: query[0].data?.environment.definition?.lastUpdatedAt,
  });

  return query;
};
